import React from "react";
import { Link } from "react-router-dom";
import { useReducedMotion } from "@/hooks/useReducedMotion";

export interface OrderFlowLogoProps {
  size?: "small" | "medium" | "large";
  showText?: boolean;
  animated?: boolean;
  className?: string;
  to?: string;
  onGradientBackground?: boolean;
}

export function OrderFlowLogo({
  size = "medium",
  showText = true,
  animated = true,
  className = "",
  to = "/",
  onGradientBackground = false,
}: OrderFlowLogoProps) {
  // Respect user's reduced-motion preference
  const shouldReduceMotion = useReducedMotion();
  const effectiveAnimated = animated && !shouldReduceMotion;
  const sizeMap = {
    small: { iconW: 44, iconH: 34, textSize: "16px" },
    medium: { iconW: 56, iconH: 44, textSize: "20px" },
    large: { iconW: 72, iconH: 56, textSize: "26px" },
  };

  const { iconW, iconH, textSize } = sizeMap[size];

  const logo = (
    <div className={`transformation-logo ${effectiveAnimated ? "animated" : ""} ${className}`} aria-label="OrderFlow logo">
      <style>{`
        .transformation-logo {
          display: flex;
          align-items: center;
          gap: 14px;
          user-select: none;
        }
        .transformation-icon {
          background: white;
          border-radius: 10px;
          display: flex;
          position: relative;
          box-shadow: 0 2px 10px rgba(0,0,0,0.12);
          padding: 8px 10px;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }
        .transformation-icon.with-glow {
          box-shadow: 0 2px 10px rgba(0,0,0,0.12), 0 0 20px rgba(102, 126, 234, 0.12);
        }
        .chaos-side {
          flex: 1;
          position: relative;
          padding-right: 6px;
          border-right: 2px solid rgba(102, 126, 234, 0.08);
          min-width: 28%;
          min-height: 100%;
        }
        .chaos-dot {
          position: absolute;
          width: 5px;
          height: 5px;
          background: linear-gradient(135deg, rgba(102,126,234,0.6) 0%, rgba(118,75,162,0.7) 100%);
          border-radius: 50%;
        }
        .chaos-dot:nth-child(1){left:3px;top:4px;transform:rotate(-15deg);}
        .chaos-dot:nth-child(2){left:12px;top:10px;width:4px;height:4px;transform:rotate(25deg);}
        .chaos-dot:nth-child(3){left:6px;top:18px;transform:rotate(-8deg);}
        .chaos-dot:nth-child(4){left:14px;top:23px;width:3px;height:3px;transform:rotate(12deg);}
        .chaos-dot:nth-child(5){left:4px;top:30px;width:4px;height:4px;transform:rotate(-20deg);}
        .order-side{
          flex:1;display:flex;flex-direction:column;align-items:center;
          justify-content:center;gap:4px;padding-left:6px;min-width:40%;
        }
        .order-bar{
          width:100%;height:4px;
          background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);
          border-radius:2px;opacity:0.95;
        }
        .order-bar:nth-child(2){width:85%;}
        .order-bar:nth-child(3){width:90%;}
        .transformation-arrow{
          position:absolute;top:50%;left:50%;
          transform:translate(-50%,-50%);
          width:10px;height:2px;
          background:linear-gradient(90deg,rgba(102,126,234,0.6) 0%,rgba(118,75,162,0.9) 100%);
          z-index:1;
        }
        .transformation-arrow::after{
          content:'';position:absolute;right:-4px;top:50%;
          transform:translateY(-50%);
          width:0;height:0;
          border-left:5px solid rgba(118,75,162,0.9);
          border-top:3px solid transparent;border-bottom:3px solid transparent;
        }
        .transformation-text{
          font-weight:700;letter-spacing:-0.4px;line-height:1;
        }
        .transformation-text.gradient{
          background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);
          -webkit-background-clip:text;-webkit-text-fill-color:transparent;
          background-clip:text;
        }
        .transformation-text.white{color:white;text-shadow:0 1px 2px rgba(0,0,0,0.08);}
        .animated .chaos-dot:nth-child(1){animation:float1 2.8s ease-in-out infinite;}
        .animated .chaos-dot:nth-child(2){animation:float2 3.2s ease-in-out infinite;}
        .animated .chaos-dot:nth-child(3){animation:float3 2.5s ease-in-out infinite;}
        .animated .chaos-dot:nth-child(4){animation:float4 3s ease-in-out infinite;}
        .animated .chaos-dot:nth-child(5){animation:float5 2.7s ease-in-out infinite;}
        @keyframes float1{0%,100%{transform:translate(0,0)rotate(-15deg);}
          33%{transform:translate(-2px,-2px)rotate(-18deg);}
          66%{transform:translate(2px,1px)rotate(-12deg);}
        }
        @keyframes float2{0%,100%{transform:translate(0,0)rotate(25deg);}
          33%{transform:translate(1px,2px)rotate(28deg);}
          66%{transform:translate(-1px,-1px)rotate(22deg);}
        }
        @keyframes float3{0%,100%{transform:translate(0,0)rotate(-8deg);}
          33%{transform:translate(-1px,2px)rotate(-11deg);}
          66%{transform:translate(2px,-1px)rotate(-5deg);}
        }
        @keyframes float4{0%,100%{transform:translate(0,0)rotate(12deg);}
          33%{transform:translate(2px,-1px)rotate(15deg);}
          66%{transform:translate(-2px,2px)rotate(9deg);}
        }
        @keyframes float5{0%,100%{transform:translate(0,0)rotate(-20deg);}
          33%{transform:translate(1px,-2px)rotate(-23deg);}
          66%{transform:translate(-1px,1px)rotate(-17deg);}
        }
        .animated .order-bar:nth-child(1){animation:glow 3s ease-in-out infinite;}
        .animated .order-bar:nth-child(2){animation:glow 3s ease-in-out 0.36s infinite;}
        .animated .order-bar:nth-child(3){animation:glow 3s ease-in-out 0.72s infinite;}
        @keyframes glow{0%,100%{opacity:0.9;box-shadow:none;}
          50%{opacity:1;box-shadow:0 0 8px rgba(102,126,234,0.22);}
        }
        .animated .transformation-arrow{animation:pulse 2s ease-in-out infinite;}
        @keyframes pulse{0%,100%{opacity:0.7;}50%{opacity:1;box-shadow:0 0 6px rgba(118,75,162,0.4);}}
      `}</style>

      <div className={`transformation-icon ${onGradientBackground ? 'with-glow' : ''}`} style={{ width: `${iconW}px`, height: `${iconH}px` }}>
        <div className="chaos-side">
          <div className="chaos-dot" />
          <div className="chaos-dot" />
          <div className="chaos-dot" />
          <div className="chaos-dot" />
          <div className="chaos-dot" />
        </div>
        <div className="transformation-arrow" />
        <div className="order-side">
          <div className="order-bar" />
          <div className="order-bar" />
          <div className="order-bar" />
        </div>
      </div>

      {showText && (
        <span
          className={`transformation-text ${onGradientBackground ? "white" : "gradient"}`}
          style={{ fontSize: textSize }}
        >
          OrderFlow
        </span>
      )}
    </div>
  );

  if (to) {
    return (
      <Link to={to} className="inline-flex items-center hover:opacity-90 transition-opacity">
        {logo}
      </Link>
    );
  }
  return logo;
}

export default OrderFlowLogo;
